// Главный поток: DOM, проверка окружения, каталог, запуск и останов.
//
// Всё, что на горячем пути, живёт не здесь: сокетом владеет Worker, звуком —
// два worklet'а. Главный поток создаёт их и уходит, потому что пауза на
// отрисовке или сборке мусора здесь означала бы пропущенный блок.

import { blockSizeFor, createCaptureContext, startCapture } from "./audio/capture.ts";
import type { Capture, CaptureContext } from "./audio/capture.ts";
import { listDevices, trackSampleRate, unwantedProcessing } from "./audio/devices.ts";
import { recommendGainDb } from "./audio/gain.ts";
import { MeterSetupError, measureInput } from "./audio/meter.ts";
import type { DeviceOption } from "./audio/devices.ts";
import { createPlaybackContext, startPlayback } from "./audio/playback.ts";
import type { Playback } from "./audio/playback.ts";
import { attachHelp, installHelpDismiss } from "./help.ts";
import {
  ForbiddenError,
  UnauthorizedError,
  fetchCatalog,
  fetchDefaults,
  fetchSession,
} from "./net/http.ts";
import type { Catalog, Defaults } from "./net/http.ts";
import { IS_LITTLE_ENDIAN, setCommand } from "./net/protocol.ts";
import { wsUrl } from "./net/wsurl.ts";
import * as settings from "./settings.ts";
import { FLOOR_DB, formatDb, thresholdMarkers } from "./ui/level.ts";
import { describeStatus, startingStep } from "./ui/status.ts";
import type { Status } from "./ui/status.ts";
import { freshTrend, isHot, observe } from "./ui/trend.ts";
import { createMeter } from "./ui/meter-view.ts";
import type {
  CaptureStats,
  FromWorker,
  LevelMessage,
  PlaybackStats,
  SessionState,
  ToWorker,
} from "./shared/messages.ts";

const RECONNECT_INTERVAL_MS = 3_000;

const el = {
  environment: document.getElementById("environment") as HTMLParagraphElement,
  who: document.getElementById("who") as HTMLOutputElement,
  login: document.getElementById("login") as HTMLButtonElement,
  gateReason: document.getElementById("gate-reason") as HTMLParagraphElement,
  gateLogout: document.getElementById("gate-logout") as HTMLButtonElement,
  logout: document.getElementById("logout") as HTMLButtonElement,
  voice: document.getElementById("voice") as HTMLSelectElement,
  inputDevice: document.getElementById("input-device") as HTMLSelectElement,
  outputDevice: document.getElementById("output-device") as HTMLSelectElement,
  pitch: document.getElementById("pitch") as HTMLInputElement,
  pitchValue: document.getElementById("pitch-value") as HTMLOutputElement,
  indexRatio: document.getElementById("index-ratio") as HTMLInputElement,
  indexRatioValue: document.getElementById("index-ratio-value") as HTMLOutputElement,
  toggle: document.getElementById("toggle") as HTMLButtonElement,
  counters: document.getElementById("counters") as HTMLParagraphElement,
  diagnostics: document.getElementById("diagnostics") as HTMLParagraphElement,
  reason: document.getElementById("reason") as HTMLParagraphElement,
  sessionReason: document.getElementById("session-reason") as HTMLParagraphElement,
  status: document.getElementById("status") as HTMLOutputElement,
  statusLabel: document.getElementById("status-label") as HTMLSpanElement,
  app: document.getElementById("app") as HTMLDivElement,
  build: document.getElementById("build") as HTMLParagraphElement,
  grantRow: document.getElementById("grant-row") as HTMLElement,
  grant: document.getElementById("grant") as HTMLButtonElement,
  measure: document.getElementById("measure") as HTMLButtonElement,
  measureValue: document.getElementById("measure-value") as HTMLOutputElement,
  gainRow: document.getElementById("gain-row") as HTMLElement,
  applyGain: document.getElementById("apply-gain") as HTMLButtonElement,
  applyGainValue: document.getElementById("apply-gain-value") as HTMLOutputElement,
  sink: document.getElementById("sink") as HTMLAudioElement,
  meterNote: document.getElementById("meter-note") as HTMLParagraphElement,
  serverInfo: document.getElementById("server-info") as HTMLParagraphElement,
};

/** Плитки счётчиков по ключу `data-key`. */
const tiles = new Map(
  Array.from(document.querySelectorAll<HTMLElement>(".tile")).map((tile) => [
    tile.dataset.key!,
    { tile, value: tile.querySelector(".tile-value") as HTMLOutputElement },
  ]),
);
const bufferFill = document.querySelector(".tile-bar-fill") as HTMLElement;

const inputMeter = createMeter(document.getElementById("meter-input")!);
const outputMeter = createMeter(document.getElementById("meter-output")!);

interface Session {
  worker: Worker;
  capture: Capture;
  captureContext: CaptureContext;
  playback: Playback;
  playbackContext: AudioContext;
}

/** Восемь счётчиков тракта плюс браузерные. */
const counters = {
  connected: false,
  uplinkBlocks: 0,
  downlinkBlocks: 0,
  uplinkDrops: 0,
  reconnects: 0,
  bufferedAmount: 0,
  captureDrops: 0,
  underruns: 0,
  overruns: 0,
  /** Занятость буфера приёма, мс; `null` — вывода нет. */
  bufferMs: null as number | null,
  /** Буфер ещё набирает префилл и не играет. */
  filling: false,
};

/** Счётчики, рост которых означает проблему. Имена — ключи `counters`. */
const TROUBLE = ["underruns", "overruns", "captureDrops", "uplinkDrops", "reconnects"] as const;
const trends = new Map(TROUBLE.map((key) => [key, freshTrend()]));

/** Что показывать про тракт: состояние сессии и путь вывода — раздельно. */
const diagnostics = { path: "" };


let defaults: Defaults | null = null;
let catalog: Catalog | null = null;
let session: Session | null = null;
/**
 * Запуск идёт прямо сейчас.
 *
 * `session` заполняется только в самом конце, поэтому без этого флага второе
 * нажатие во время запуска начинало бы вторую сессию: два захвата, два
 * воркера, четыре аудиоконтекста. Выглядит это намертво зависшей страницей.
 */
let starting = false;
/**
 * Частота дорожки микрофона за текущую сессию.
 *
 * Отдельно от `session`, потому что нужна и в строке тракта, и в заметке о
 * узкой полосе, а `session` заполняется только в самом конце запуска.
 */
let micSampleRate: number | null = null;

/** Причина сбоя сессии — под статусом, рядом с кнопкой. */
function showReason(text: string | null): void {
  el.sessionReason.textContent = text ?? "";
  el.sessionReason.classList.toggle("hidden", text === null);
}

/** Причина уровня страницы: каталог не прочитан, и `#app` скрыт целиком. */
function showPageReason(text: string | null): void {
  el.reason.textContent = text ?? "";
  el.reason.classList.toggle("hidden", text === null);
}

function setStatus(status: Status): void {
  el.status.dataset.tone = status.tone;
  el.statusLabel.textContent = status.label;
  // Точка в заголовке видна и из фоновой вкладки: микрофон открыт, звук идёт
  // на сервер. Иначе забытая вкладка держит слот и видеопамять незаметно.
  const live = status.tone !== "idle" && status.tone !== "error";
  document.title = live ? "● rustvoice" : "rustvoice";
}

/** Кнопка запуска: подпись и вид — вместе, чтобы не разъехались. */
function setToggle(running: boolean): void {
  el.toggle.textContent = running ? "Выключить" : "Включить";
  el.toggle.dataset.running = String(running);
}

function showNotice(text: string | null): void {
  el.environment.textContent = text ?? "";
  el.environment.classList.toggle("hidden", text === null);
}

/**
 * Проверить, что браузер вообще способен вытянуть задуманное.
 *
 * Отказать внятно здесь дешевле, чем оставить пользователя с неработающей
 * кнопкой: `navigator.mediaDevices` отсутствует целиком, если страница открыта
 * не в безопасном контексте, и никакого другого признака этому нет.
 */
function checkEnvironment(): string | null {
  if (!IS_LITTLE_ENDIAN) {
    return "Эта машина big-endian, а протокол little-endian. Звук уехал бы перевёрнутым, поэтому не запускаемся.";
  }
  if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia) {
    return (
      "Микрофон недоступен: страница открыта не в безопасном контексте. " +
      "Нужен https:// либо http://localhost — см. раздел «Веб-клиент» в README."
    );
  }
  if (typeof AudioWorkletNode === "undefined") {
    return "Браузер не поддерживает AudioWorklet — обработка звука невозможна.";
  }
  return null;
}

// --- каталог -----------------------------------------------------------------

function fillVoices(catalog: Catalog): void {
  el.voice.replaceChildren();

  if (catalog.voices.length === 0) {
    el.voice.append(new Option("сервер без голосов — звук пройдёт неизменным", ""));
    el.voice.disabled = true;
    return;
  }

  for (const voice of catalog.voices) {
    // Пометка про индекс не украшение: без него index_ratio отвергается
    // в хендшейке, и знать об этом надо до подключения.
    el.voice.append(new Option(voice.has_index ? `${voice.id} (с индексом)` : voice.id, voice.id));
  }

  // Список наполнился только сейчас, поэтому сохранённый выбор применяем
  // здесь: на момент общего восстановления ни одного <option> ещё не было.
  const saved = settings.saved("voice");
  if (saved !== null && catalog.voices.some((v) => v.id === saved)) {
    el.voice.value = saved;
  }
  el.voice.disabled = false;
}

/**
 * Ретривал доступен только у голоса с индексом.
 *
 * Сервер на `index_ratio > 0` без индекса отвечает отказом — и правильно
 * делает, иначе жалоба звучала бы как «индекс ничего не меняет». Но узнать об
 * этом отказом хуже, чем не дать выкрутить ползунок: `has_index` приезжает в
 * каталоге, и запрет виден сразу.
 */
function syncIndexControl(): void {
  const voice = catalog?.voices.find((v) => v.id === el.voice.value);
  const available = voice?.has_index === true;

  el.indexRatio.disabled = !available;
  if (!available) {
    el.indexRatio.value = "0";
  }
  renderIndexRatio(available);
}

function renderIndexRatio(available: boolean): void {
  if (!available) {
    el.indexRatioValue.textContent = "у голоса нет индекса";
    return;
  }
  const ratio = Number(el.indexRatio.value);
  el.indexRatioValue.textContent =
    ratio === 0 ? "выключен" : `${Math.round(ratio * 100)}% от индекса`;
}

function fillDevices(
  select: HTMLSelectElement,
  options: DeviceOption[],
  key: "inputDevice" | "outputDevice",
): void {
  const saved = settings.saved(key);
  select.replaceChildren();
  select.append(new Option("системный по умолчанию", ""));
  for (const option of options) {
    select.append(new Option(option.label, option.deviceId));
  }
  // Устройство могло исчезнуть между запусками — тогда откатываемся на
  // системное по умолчанию, а не оставляем выбранным несуществующее.
  select.value = saved !== null && options.some((o) => o.deviceId === saved) ? saved : "";
  select.disabled = false;
}

/** Перечисление уже идёт — второе только помешает. */
let listingDevices = false;

/**
 * Перечислить устройства и наполнить списки. Разрешения **не просит**.
 *
 * Имена устройств браузер отдаёт только тому, кому выдано разрешение на
 * микрофон. Если оно уже выдано (обычное дело на повторном заходе), имена
 * приезжают сразу и выбор виден до всякого запуска. Если нет — списки
 * получатся безымянными, и тогда показывается кнопка: спросить разрешение
 * можно только по нажатию, см. `grantMicrophone`.
 */
async function refreshDevices(): Promise<void> {
  if (listingDevices) {
    return;
  }
  listingDevices = true;

  try {
    const devices = await listDevices();
    fillDevices(el.inputDevice, devices.inputs, "inputDevice");
    fillDevices(el.outputDevice, devices.outputs, "outputDevice");

    // `listDevices` подставляет «Микрофон N» вместо пустых имён, так что по
    // такой подписи и видно, что разрешения ещё нет.
    const named = devices.inputs.some((d) => !d.label.startsWith("Микрофон "));
    el.grantRow.classList.toggle("hidden", named || devices.inputs.length === 0);
  } catch {
    // Перечисление может быть запрещено политикой — не повод падать.
  } finally {
    listingDevices = false;
    // Списки наполняются и во время сессии: наушники втыкают на ходу, и
    // `fillDevices` отпирает оба селекта разом. Выходу это и нужно — он
    // переключается сразу, — а микрофон читается один раз при подключении, и
    // живой список посреди сессии обещал бы смену, которой не будет. Замок
    // поэтому восстанавливается здесь же, а не только там, где ставился.
    settings.lockHandshakeOnly(session !== null || starting);
  }
}

/**
 * Спросить разрешение на микрофон и показать настоящие имена устройств.
 *
 * Вызывать **только из обработчика нажатия**. Автоматический запрос — тот, что
 * без жеста пользователя, — браузер вправе оставить без ответа навсегда: если
 * человек не выбрал в окошке ни «разрешить», ни «запретить», промис не
 * разрешается и не отклоняется. Так и было: страница ждала вечно, устройства
 * не появлялись, а следующий запрос вставал в очередь за первым, и выглядело
 * это зависанием. Отдельно неприятно, что окошко в фоновой вкладке вообще не
 * показывается, пока на неё не переключатся.
 */
async function grantMicrophone(): Promise<void> {
  el.grant.disabled = true;
  let stream: MediaStream | null = null;

  try {
    stream = await navigator.mediaDevices.getUserMedia({ audio: true });
  } catch {
    // Отказали или устройства нет — останется системное по умолчанию.
  } finally {
    // Гасим сразу: держать микрофон открытым только ради имён нельзя, иначе
    // индикатор записи горит всё время, пока страница открыта, а другие
    // вкладки не могут получить устройство.
    stream?.getTracks().forEach((track) => track.stop());
    el.grant.disabled = false;
  }

  await refreshDevices();
}

async function loadCatalog(): Promise<void> {
  showPageReason(null);

  try {
    const [loadedCatalog, loaded] = await Promise.all([fetchCatalog(), fetchDefaults()]);
    defaults = loaded;
    catalog = loadedCatalog;
    fillVoices(loadedCatalog);
    syncIndexControl();

    // Границы контролов — от сервера, а не хардкодом: разъехаться этим двум
    // наборам нельзя, иначе интерфейс предложит заведомо отказное.
    el.pitch.min = String(-loaded.limits.max_pitch_shift);
    el.pitch.max = String(loaded.limits.max_pitch_shift);
    el.pitch.disabled = false;
    el.app.classList.remove("hidden");
    void refreshDevices();
    settings.applyLimits(loaded.limits);
    (settings.knobs.protect!.input as HTMLInputElement).disabled = false;
    el.toggle.disabled = false;

    // Silero задаётся флагом сервера и есть не везде. Умолчанием стоит он, так
    // что без модели первое же подключение провалилось бы отказом.
    showNotice(settings.applyCatalog(loadedCatalog.has_silero_vad));
    syncDerived();

    // Замок восстанавливаем последним действием: всё, что выше расставило
    // `disabled = false` (список голосов, тон, защита согласных, кнопка),
    // иначе сняло бы замок с того, что применяется только в рукопожатии.
    // Снаружи это выглядит как живой выбор голоса, который ничего не меняет:
    // значение прочитано один раз при подключении.
    settings.lockHandshakeOnly(session !== null || starting);

    el.serverInfo.textContent =
      `сервер отвечает: голосов ${loadedCatalog.voices.length}` +
      `, silero ${loadedCatalog.has_silero_vad ? "есть" : "нет"}` +
      `, блок ${loaded.session.block_ms} мс`;
  } catch (error) {
    defaults = null;
    catalog = null;
    // Прячем обратно: без доступа ни одна ручка ничего не делает.
    el.app.classList.add("hidden");
    el.voice.replaceChildren();
    el.voice.disabled = true;
    el.toggle.disabled = true;
    if (error instanceof UnauthorizedError) {
      // Сессия кончилась — снова экран входа, с одной строкой причины.
      showGate("Сессия истекла — войдите заново.");
      return;
    }
    if (error instanceof ForbiddenError) {
      showNoAccess();
      return;
    }
    showPageReason(`Не удалось прочитать каталог: ${String(error)}`);
  }
}

// --- счётчики ----------------------------------------------------------------

/**
 * Поставить метки порогов на индикатор входа.
 *
 * Зовётся на любую подкрутку ручек, а не только во время сессии: порог
 * выставляют как раз при проверке микрофона, глядя на полосу.
 */
function syncMarkers(): void {
  const current = settings.thresholdSettings();
  const markers = thresholdMarkers(current);
  inputMeter.setMarkers(markers);

  const parts = markers.map(
    (m) => `${m.label} ${formatDb(m.db)}${m.db < FLOOR_DB ? " (ниже шкалы)" : ""}`,
  );
  let note = parts.length > 0 ? `Метки на входе: ${parts.join(", ")}.` : "";
  if (parts.length > 0 && current.inputGainDb !== 0) {
    // Иначе человек увидит «гейт −52» при выставленных −40 и решит, что
    // индикатор врёт.
    const gain = `${current.inputGainDb > 0 ? "+" : ""}${current.inputGainDb} дБ`;
    note += ` Сдвинуты на усиление ${gain}: индикатор меряет до него, сервер — после.`;
  }
  if (current.vad === "silero") {
    note += `${note === "" ? "" : " "}У Silero метки нет: его порог — вероятность речи, а не уровень.`;
  }
  el.meterNote.textContent = note;
}

function setTile(key: string, text: string, tone: string): void {
  const entry = tiles.get(key);
  if (entry === undefined) {
    return;
  }
  entry.value.textContent = text;
  entry.tile.dataset.tone = tone;
}

/**
 * Пометить свёрнутые разделы, где что-то сдвинуто с умолчаний.
 *
 * «Звучит глухо» при гейте, забытом в закрытом разделе, ищут на сервере: сама
 * ручка не видна, а главный экран выглядит как обычно.
 */
function syncChanged(): void {
  for (const summary of document.querySelectorAll(".summary-changed")) {
    const names = settings.changedIn(summary.closest("details")!);
    summary.textContent = names.length > 0 ? ` · изменено: ${names.join(", ")}` : "";
  }
}

/** Всё, что выводится из положения ручек: метки индикатора и пометки разделов. */
function syncDerived(): void {
  syncMarkers();
  syncChanged();
}

function renderCounters(): void {
  const now = Date.now();
  const live = session !== null || starting;

  if (!live) {
    setTile("connected", "—", "idle");
  } else if (counters.connected) {
    setTile("connected", "есть", "ok");
  } else {
    setTile("connected", "нет", "warn");
  }

  for (const key of TROUBLE) {
    const trend = trends.get(key)!;
    observe(trend, counters[key], now);
    setTile(key, String(counters[key]), isHot(trend, now) ? "warn" : "idle");
  }

  // Полоса буфера — от нуля до двух глубин jitter-буфера, цель посередине:
  // видно и «запаса нет», и «копится задержка».
  if (counters.bufferMs === null) {
    setTile("buffer", "—", "idle");
    bufferFill.style.width = "0";
  } else {
    const target = settings.jitterMs();
    // «Набирает» — цветом плитки, а не словом: слово переносилось и
    // вытягивало всю строку плиток в высоту.
    setTile("buffer", `${counters.bufferMs} мс`, counters.filling ? "busy" : "idle");
    bufferFill.style.width = `${Math.min(100, (counters.bufferMs / (2 * target)) * 100)}%`;
  }

  el.counters.textContent =
    `связь=${counters.connected ? "да" : "нет"}` +
    ` вверх=${counters.uplinkBlocks} вниз=${counters.downlinkBlocks}` +
    ` срывы=${counters.underruns} перепол.=${counters.overruns}` +
    ` вход-перепол.=${counters.captureDrops} потери=${counters.uplinkDrops}` +
    ` реконнекты=${counters.reconnects} очередь=${counters.bufferedAmount}Б` +
    (counters.bufferMs === null ? "" : ` буфер=${counters.bufferMs}мс`);
}

function renderDiagnostics(): void {
  el.diagnostics.textContent = diagnostics.path;
}

// --- сессия ------------------------------------------------------------------

/** Когда началось ожидание ответа сервера. */
let waitingSince = 0;
let waitingTimer: ReturnType<typeof setInterval> | undefined;
/**
 * Последнее состояние сессии.
 *
 * Нужно счётчику ожидания: таймер живёт дольше сообщения, на котором его
 * завели, и захватывать состояние в замыкание нельзя. Первым в серии всегда
 * идёт `connecting`, поэтому захваченное замыкание раз в секунду переписывало
 * «сервер грузит модели… N с» обратно на «подключение…» — то самое замершее
 * окно, ради устранения которого счётчик и заводили.
 */
let waitingStatus: SessionState = { state: "idle" };

function renderState(status: SessionState): void {
  const waited = waitingSince === 0 ? 0 : Math.round((Date.now() - waitingSince) / 1000);
  setStatus(describeStatus(status, waited));
}

/** Строка про тракт: путь вывода и задержки. Первое, что спросят при жалобе. */
function describePath(
  playback: Playback,
  capture: AudioContext,
  output: AudioContext,
  micRate: number | null,
): string {
  const ms = (value: number | undefined) => (value ? `${(value * 1000).toFixed(1)} мс` : "—");
  const sink = playback.path === "context" ? "setSinkId" : "<audio> (Firefox, +буфер)";
  // Частота дорожки — только когда она узкая. Наше собственное прореживание
  // при экономии канала уже видно по частоте контекста, а 44.1 против 48 —
  // обычное дело и ни на что не влияет.
  const mic = isNarrowband(micRate) ? ` (микрофон ${micRate} Гц)` : "";
  return (
    `вывод: ${sink} · вход ${capture.sampleRate} Гц${mic} · выход ${output.sampleRate} Гц` +
    ` · base ${ms(output.baseLatency)} · out ${ms(output.outputLatency)}`
  );
}

/**
 * Ниже какой частоты дорожка считается узкополосной.
 *
 * Не «ниже, чем у контекста»: 44.1 против 48 — обычное дело, речь от этого не
 * страдает, и пугать этим нельзя. А вот гарнитурный профиль Bluetooth даёт
 * 8, 16 или 24 кГц — и вот там верхней половины спектра уже нет.
 */
const NARROWBAND_RATE = 32_000;

function isNarrowband(micRate: number | null): boolean {
  return micRate !== null && micRate < NARROWBAND_RATE;
}

/**
 * Узкополосный микрофон объясняет «звук глухой» и ничем другим не виден.
 *
 * Браузер честно растянет 16 кГц до частоты контекста, форма сигнала будет
 * правильной, счётчики — нулевыми, а верхней половины спектра в нём уже нет.
 * Нативный клиент в этой ситуации просто не стартует, поэтому ловушка чисто
 * браузерная.
 */
function narrowbandNotice(micRate: number | null): string | null {
  if (!isNarrowband(micRate)) {
    return null;
  }
  return (
    `Микрофон отдаёт ${micRate} Гц: браузер растянет сигнал, но верхней половины ` +
    "спектра в нём уже нет — звук будет глухим. Обычно это Bluetooth-гарнитура в " +
    "гарнитурном профиле: берите микрофон отдельно от наушников."
  );
}

function onWorkerMessage(message: FromWorker): void {
  switch (message.type) {
    case "state": {
      waitingStatus = message.status;
      if (message.status.state === "connecting" || message.status.state === "loading") {
        if (waitingSince === 0) {
          waitingSince = Date.now();
          // Пока сервер грузит модели, сообщений от него нет, а отсчёт должен
          // идти: иначе экран замирает и выглядит зависшим.
          waitingTimer = setInterval(() => renderState(waitingStatus), 1000);
        }
      } else {
        clearInterval(waitingTimer);
        waitingSince = 0;
      }
      renderState(message.status);
      if (message.status.state === "failed") {
        void stopSession();
        // Сессия кончилась на сервере при открытой странице. Показать причину
        // мало: кнопка «Войти» живёт на экране входа, и без возврата туда
        // человеку остаётся только перезагружать страницу руками.
        if (message.status.code === "unauthorized") {
          showGate("Сессия истекла — войдите заново.");
        } else if (message.status.code === "forbidden") {
          // Доступ отобрали при открытой странице.
          showNoAccess();
        } else {
          showReason(message.status.reason);
        }
      }
      break;
    }
    case "accepted": {
      const { accepted } = message;
      showReason(null);
      if (session !== null) {
        diagnostics.path =
          describePath(
            session.playback,
            session.captureContext.context,
            session.playbackContext,
            micSampleRate,
          ) +
          (accepted.model_sr ? ` · модель ${accepted.model_sr} Гц` : "") +
          ` · ${accepted.server_info.gpu ?? "cpu"}`;
        renderDiagnostics();
      }
      break;
    }
    case "control-error": {
      showReason(message.reason);
      break;
    }
    case "stats": {
      counters.connected = message.connected;
      counters.uplinkBlocks = message.uplinkBlocks;
      counters.downlinkBlocks = message.downlinkBlocks;
      counters.uplinkDrops = message.uplinkDrops;
      counters.reconnects = message.reconnects;
      counters.bufferedAmount = message.bufferedAmount;
      renderCounters();
      break;
    }
  }
}

/**
 * Обернуть шаг запуска пределом ожидания.
 *
 * Браузерные разрешения и устройства умеют не отвечать вовсе: `getUserMedia`
 * по спецификации вправе не разрешить и не отклонить промис, а открытие
 * занятого устройства подвисает на драйвере. Без предела это выглядит как
 * намертво зависшая страница, по которой ничего не понять. С ним — понятная
 * строка о том, какой именно шаг не ответил.
 */
/**
 * Показать, какой шаг запуска идёт прямо сейчас.
 *
 * Без этого любая заминка выглядит одинаково — «ничего не происходит», — и
 * непонятно даже, на чьей она стороне. С подписью видно сразу: встали на
 * микрофоне, на выводе или уже на сервере.
 */
function progress(what: string): void {
  setStatus(startingStep(what));
}

async function step<T>(
  name: string,
  ms: number,
  work: Promise<T>,
  cleanup?: (value: T) => void,
): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  let expired = false;
  const limit = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      expired = true;
      reject(new Error(`${name}: нет ответа ${ms / 1000} с`));
    }, ms);
  });

  try {
    return await Promise.race([work, limit]);
  } finally {
    clearTimeout(timer);

    // Предел — не отмена: `Promise.race` проигравшего не трогает, работа идёт
    // дальше и однажды всё-таки завершается. Принять результат к этому моменту
    // уже некому, а вернуть он может живое устройство: человек отошёл от
    // запроса разрешения, вернулся через полминуты и нажал «Разрешить» —
    // дорожка открывается, ссылки на неё нет ни у кого, микрофон занят и
    // индикатор записи горит до перезагрузки страницы. Закрытые к тому времени
    // контексты дорожку не останавливают.
    if (expired && cleanup !== undefined) {
      void work.then(cleanup, () => {
        // Опоздавшая работа отвалилась сама — убирать нечего.
      });
    }
  }
}

/** Идёт ли проверка микрофона; вторая одновременно только помешает. */
let measuring: (() => void) | null = null;

/**
 * Усиление, посчитанное по последнему замеру. `null` — советовать нечего.
 *
 * Считается, но не применяется само: усиление слышно, и менять звук без
 * нажатия — это ровно та тихая подмена, от которой в остальном проект
 * отказывается.
 */
let advisedGainDb: number | null = null;

/** Показать (или убрать) совет по усилению. */
function showGainAdvice(reading: { rms: number; peak: number }): void {
  // Во время сессии советовать нечего: усиление уехало в хендшейке, и сервер
  // живёт с ним до переподключения. Кнопка переставила бы запертый ползунок,
  // черты порогов переехали бы на новое усиление, а сервер резал бы по
  // старому — ровно та ловушка, от которой черты и защищают.
  if (session !== null || starting) {
    advisedGainDb = null;
    el.gainRow.classList.add("hidden");
    return;
  }

  const knob = settings.knobs.inputGain!.input as HTMLInputElement;
  const current = Number(knob.value);
  const advice = recommendGainDb(reading, Number(knob.min), Number(knob.max));

  // Совет даётся относительно нуля, а ползунок мог быть уже подкручен: без
  // поправки повторный замер советовал бы добавить то, что уже добавлено.
  advisedGainDb =
    advice === null ? null : Math.min(Number(knob.max), Math.max(Number(knob.min), advice + current));

  const nothing = advisedGainDb === null || advisedGainDb === current;
  el.gainRow.classList.toggle("hidden", nothing);
  if (nothing) {
    return;
  }

  const value = advisedGainDb!;
  el.applyGain.textContent = value > current ? "Поднять усиление" : "Опустить усиление";
  el.applyGainValue.textContent = `до ${value > 0 ? "+" : ""}${value} дБ`;
}

/** Применить посчитанный совет к ползунку. */
function applyGainAdvice(): void {
  if (advisedGainDb === null || session !== null || starting) {
    return;
  }
  const knob = settings.knobs.inputGain!.input as HTMLInputElement;
  knob.value = String(advisedGainDb);
  settings.renderKnob(settings.knobs.inputGain!);
  settings.save();
  syncDerived();
  el.gainRow.classList.add("hidden");
}

/**
 * Проверить микрофон, не поднимая сессию.
 *
 * Отвечает на единственный вопрос, который иначе стоит секунд загрузки
 * моделей: слышит ли браузер что-нибудь вообще. Заодно показывает то самое
 * число, с которым сравнивается порог детектора речи, — выставлять его на глаз
 * иначе невозможно.
 */
async function checkMicrophone(): Promise<void> {
  if (measuring) {
    measuring();
    return;
  }

  el.measure.textContent = "Остановить";
  el.measureValue.textContent = "говорите…";
  el.gainRow.classList.add("hidden");

  try {
    measuring = await measureInput({
      deviceId: el.inputDevice.value !== "" ? el.inputDevice.value : null,
      buildTag: __BUILD__,
      // Во время сессии индикатор кормит захват: второй источник того же
      // микрофона дал бы только дрожание полосы.
      onLevel: ({ rms, peak }) => {
        if (session === null && !starting) {
          inputMeter.feed(rms, peak);
        }
      },
      onDone: (best, sampleRate) => {
        measuring = null;
        // Во время сессии индикатор кормит захват, и гасить его нельзя.
        if (session === null) {
          inputMeter.reset();
        }
        el.measure.textContent = "Проверить микрофон";
        el.measureValue.textContent =
          best.rms > 0.001
            ? `максимум ${best.rms.toFixed(3)} — порог ставьте вдвое ниже`
            : "тишина: браузер ничего не слышит на этом микрофоне";
        showGainAdvice(best);
        // Узкая полоса важнее уровня: её не слышно как поломку, только как
        // «модель звучит глухо», и искать будут на сервере.
        showNotice(narrowbandNotice(sampleRate));
      },
    });
  } catch (error) {
    measuring = null;
    el.measure.textContent = "Проверить микрофон";
    el.measureValue.textContent = "";
    // «Не открыть микрофон» — только когда его действительно не открыли. После
    // MeterSetupError разрешение уже выдано, и отправлять человека проверять
    // его значит увести от причины.
    showReason(
      error instanceof MeterSetupError
        ? `Микрофон открылся, но проверка не собралась: ${String(error.message)}`
        : `Не открыть микрофон: ${String(error)}`,
    );
  }
}

/**
 * Забыть выбранный выход после неудачи.
 *
 * Сохранённое устройство проверяется по списку, так что несуществующее сюда не
 * попадёт. А вот существующее, но не отвечающее на переключение — попадёт, и
 * без сброса та же беда повторялась бы при каждом запуске: выбор ведь
 * сохранён. Возвращаем системный выход, чтобы следующая сессия начиналась с
 * заведомо рабочего.
 */
function forgetOutputDevice(text: string): void {
  showReason(`${text} Выбор сброшен — укажите выход заново, если нужен другой.`);
  el.outputDevice.value = "";
  settings.save();
}

async function startSession(
  captureContext: CaptureContext,
  playbackContext: AudioContext,
): Promise<void> {
  if (defaults === null) {
    throw new Error("сначала проверьте токен");
  }

  // Размер блока — от фактической частоты контекста, а не от умолчания
  // сервера: то в сэмплах, и при экономии канала 480 означали бы уже не 10 мс,
  // а 30.
  const blockSize = blockSizeFor(captureContext.context.sampleRate);

  progress("открываю микрофон…");
  const capture = await step(
    "микрофон",
    20_000,
    startCapture({
      context: captureContext.context,
      deviceId: el.inputDevice.value !== "" ? el.inputDevice.value : null,
      blockSize,
      buildTag: __BUILD__,
      onLost: () => {
        showReason("Микрофон пропал или разрешение отозвали.");
        void stopSession();
      },
    }),
    // Захват стоит до общего `try` ниже — до его начала `capture` ещё не
    // существует, — поэтому опоздавший приезжает сюда и здесь же снимается.
    (late) => late.stop(),
  );

  // Всё, что создаётся дальше, эта функция обязана убрать за собой сама.
  //
  // Наружу отсюда не выходит ничего: `capture`, `playback` и `worker` живут в
  // локальных переменных, а в `session` попадают последним действием. Стоит
  // упасть или упереться в предел любому шагу после открытия микрофона — и до
  // них не дотянется уже никто, включая `stopSession()`: он выходит сразу,
  // если `session` пуст. Дорожка при этом остаётся открытой, микрофон занят и
  // индикатор записи горит до перезагрузки страницы, а воркер, если успел
  // подняться, продолжает слать блоки на сервер, пока кнопка говорит
  // «Включить». Закрытие контекстов, которое делает обработчик ошибки снаружи,
  // дорожку не останавливает.
  let playback: Playback | null = null;
  let worker: Worker | null = null;

  try {
    // Захват уже идёт, значит разрешение выдано и имена устройств доступны —
    // самое время обновить списки и убрать кнопку с просьбой их показать.
    //
    // Предел ожидания здесь такой же, как у остальных шагов: перечисление
    // устройств тоже умеет не отвечать, а без него запуск встанет молча — как
    // раз между открытым микрофоном и ещё не созданным сокетом.
    progress("читаю список устройств…");
    await step("список устройств", 10_000, refreshDevices());

    progress("готовлю вывод звука…");
    playback = await step(
      "вывод звука",
      15_000,
      startPlayback({
        context: playbackContext,
        deviceId: el.outputDevice.value,
        jitterMs: settings.jitterMs(),
        buildTag: __BUILD__,
        element: el.sink,
        onSinkProblem: forgetOutputDevice,
      }),
      // Опоздавший вывод сегодня уходит вместе с закрытым контекстом, но
      // полагаться на это значит полагаться на то, что там нечего освобождать
      // отдельно. Завтра появится.
      (late) => late.stop(),
    );

    capture.node.port.onmessage = (event: MessageEvent<CaptureStats | LevelMessage>) => {
      if (event.data.type === "capture-stats") {
        counters.captureDrops = event.data.dropped;
        renderCounters();
      } else if (event.data.type === "level") {
        inputMeter.feed(event.data.rms, event.data.peak);
      }
    };
    playback.node.port.onmessage = (event: MessageEvent<PlaybackStats | LevelMessage>) => {
      if (event.data.type === "level") {
        outputMeter.feed(event.data.rms, event.data.peak);
      } else if (event.data.type === "playback-stats") {
        counters.underruns = event.data.underruns;
        counters.overruns = event.data.overruns;
        // Занятость буфера в миллисекундах: по ней сразу видно, мал ли запас и
        // крутится ли worklet вывода вообще.
        counters.bufferMs = Math.round((event.data.available / playbackContext.sampleRate) * 1000);
        counters.filling = !event.data.playing;
        renderCounters();
      }
    };

    progress("подключаюсь к серверу…");
    worker = new Worker(`socket-worker.js?v=${__BUILD__}`);
    worker.onmessage = (event: MessageEvent<FromWorker>) => onWorkerMessage(event.data);

    // Каналы напрямую worklet ↔ воркер: через главный поток блоки шли бы ровно
    // через тот поток, которого мы избегаем.
    const uplink = new MessageChannel();
    const downlink = new MessageChannel();
    const ports: ToWorker = { type: "ports", uplink: uplink.port2, downlink: downlink.port1 };
    worker.postMessage(ports, [uplink.port2, downlink.port1]);
    capture.node.port.postMessage({ type: "port", port: uplink.port1 }, [uplink.port1]);
    playback.node.port.postMessage({ type: "port", port: downlink.port2 }, [downlink.port2]);

    const start: ToWorker = {
      type: "start",
      url: wsUrl("/ws", location),
      // Токена у страницы нет: сессию несёт cookie, и браузер сам приложит её
      // к рукопожатию. Поле в протоколе осталось и едет пустым.
      token: "",
      session: {
        ...defaults.session,
        ...settings.toSessionConfig(),
        model_id: el.voice.value,
        block_size: blockSize,
        pitch_shift: Number(el.pitch.value),
        index_ratio: Number(el.indexRatio.value),
        // Фактические частоты контекстов, а не запрошенные: браузер вправе дать
        // не то, что просили, и сервер построил бы пайплайн под другую частоту.
        input_sample_rate: captureContext.context.sampleRate,
        output_sample_rate: playbackContext.sampleRate,
      },
      reconnectIntervalMs: RECONNECT_INTERVAL_MS,
    };
    worker.postMessage(start);

    session = { worker, capture, captureContext, playback, playbackContext };
    // Всё, что применяется только на хендшейке, на время сессии блокируется:
    // иначе крутишь ползунок, ничего не меняется, и виноват как будто сервер.
    settings.lockHandshakeOnly(true);

    micSampleRate = trackSampleRate(capture.track);
    const unwanted = unwantedProcessing(capture.track);

    // Заметки копятся списком: раньше они шли лесенкой из тернарников, и вторая
    // причина молчала, пока не исчезнет первая.
    const notices = [
      unwanted.length > 0
        ? `Браузер не выключил: ${unwanted.join(", ")}. На вход модели идёт уже обработанный сигнал.`
        : null,
      captureContext.rateRefused
        ? `Браузер отказал в ${captureContext.requestedRate} Гц, шлю ${captureContext.context.sampleRate} Гц.`
        : null,
      narrowbandNotice(micSampleRate),
    ].filter((notice): notice is string => notice !== null);

    showNotice(notices.length > 0 ? notices.join(" ") : null);
    diagnostics.path = describePath(playback, captureContext.context, playbackContext, micSampleRate);
    renderDiagnostics();
  } catch (error) {
    // Порядок тот же, что в `stopSession`: сначала попросить воркер закрыть
    // сокет, потом снять узлы, потом убить сам воркер.
    session = null;
    micSampleRate = null;
    worker?.postMessage({ type: "stop" } satisfies ToWorker);
    capture.stop();
    playback?.stop();
    worker?.terminate();
    throw error;
  }
}

async function stopSession(): Promise<void> {
  const current = session;
  session = null;
  if (current === null) {
    return;
  }

  current.worker.postMessage({ type: "stop" } satisfies ToWorker);
  current.capture.stop();
  current.playback.stop();
  current.worker.terminate();
  // Контексты именно закрываем: незакрытый держит устройство, и второй запуск
  // в Firefox может не получить микрофон.
  await Promise.allSettled([current.captureContext.context.close(), current.playbackContext.close()]);

  micSampleRate = null;
  settings.lockHandshakeOnly(false);
  clearInterval(waitingTimer);
  waitingSince = 0;
  waitingStatus = { state: "idle" };
  setToggle(false);
  // Сбой уже нарисован красным, и затирать его серым «выключено» нельзя:
  // `stopSession` зовётся и из ветки `failed`.
  if (el.status.dataset.tone !== "error") {
    setStatus(describeStatus({ state: "idle" }, 0));
  }
  diagnostics.path = "";
  renderDiagnostics();
  counters.connected = false;
  counters.bufferMs = null;
  // Сессии нет — нет и «растёт прямо сейчас». Без сброса плитка, загоревшаяся
  // в последние секунды, так и осталась бы гореть: отчётов больше не будет.
  for (const trend of trends.values()) {
    trend.changedAt = -Infinity;
  }
  renderCounters();
  inputMeter.reset();
  outputMeter.reset();
}

function onToggle(): void {
  if (starting) {
    // Уже запускаемся. Второй заход всё сломает, а сказать об этом надо.
    showReason("Запуск уже идёт — дождитесь или обновите страницу.");
    return;
  }
  if (session !== null) {
    void stopSession();
    return;
  }

  // Синхронная часть: контексты создаются и будятся внутри жеста, до первого
  // await. Иначе транзиентная активация истечёт, контекст останется suspended,
  // и process() не вызовется ни разу. По той же причине здесь же play() у
  // запасного элемента вывода — подставить ему поток можно и позже.
  const captureContext = createCaptureContext(settings.thrifty());
  const playbackContext = createPlaybackContext();
  void captureContext.context.resume();
  void playbackContext.resume();
  el.sink.play().catch(() => {
    /* элемент пуст — в Chromium этот путь и не понадобится */
  });

  setToggle(true);
  showReason(null);
  starting = true;
  // Совет по усилению, оставшийся от замера до запуска, применять уже поздно:
  // усиление уедет в хендшейке тем, что стоит сейчас.
  advisedGainDb = null;
  el.gainRow.classList.add("hidden");

  void startSession(captureContext, playbackContext)
    .catch(async (error: unknown) => {
      showReason(`Не запустилось: ${String(error)}`);
      setStatus({ tone: "error", label: "не запустилось" });
      session = null;
      settings.lockHandshakeOnly(false);
      await Promise.allSettled([captureContext.context.close(), playbackContext.close()]);
      setToggle(false);
    })
    .finally(() => {
      starting = false;
      // Замок пересчитывается последним действием, уже с обнулённым `starting`.
      //
      // Снятый выше, он мог вернуться обратно: пока закрываются контексты,
      // прилетевший `devicechange` доводит до `refreshDevices()`, а та читает
      // `starting` — тот ещё говорит «идёт запуск» — и запирает ручки. Сессии
      // при этом нет, и пересчитать замок больше некому: ручки остались бы
      // серыми до следующего `devicechange`, «Проверить» или удачного пуска.
      // Прилетает же он как раз от переткнутого устройства, то есть от самой
      // частой причины неудачного запуска.
      //
      // Снятие в ветке ошибки при этом остаётся: оно происходит до закрытия
      // контекстов, а `close()` умеет не отвечать — ждать его, чтобы отпустить
      // интерфейс, значит поменять узкую гонку на широкое зависание.
      settings.lockHandshakeOnly(session !== null);
    });
}

// --- запуск ------------------------------------------------------------------

function main(): void {
  // Прячем сразу: в разметке блок видим намеренно, см. комментарий там. Пока
  // сервер не сказал, вошёл ли человек, не показываем ничего — иначе мелькнёт
  // кнопка у вошедшего или интерфейс у невошедшего.
  el.app.classList.add("hidden");
  document.body.dataset.state = "loading";

  const problem = checkEnvironment();
  if (problem !== null) {
    // Со звуком в этом браузере не выйдет ничего, и входить незачем: кнопка
    // остаётся, но неактивная, а под ней — причина.
    showGate(problem);
    el.login.disabled = true;
    return;
  }

  el.build.textContent = `сборка ${__BUILD__}`;

  // Вход делает сервер: он уводит на Keycloak и возвращает обратно с cookie.
  el.login.addEventListener("click", () => {
    el.login.disabled = true;
    location.assign("/auth/login");
  });
  el.logout.addEventListener("click", signOut);
  el.gateLogout.addEventListener("click", signOut);
  el.toggle.addEventListener("click", onToggle);
  el.grant.addEventListener("click", () => void grantMicrophone());
  el.measure.addEventListener("click", () => void checkMicrophone());
  el.applyGain.addEventListener("click", applyGainAdvice);

  // Тон меняется на лету, без переподключения: сервер применяет его атомиком.
  el.pitch.addEventListener("input", () => {
    // Подпись рисует общий цикл выше; здесь только отправка на сервер.
    session?.worker.postMessage({
      type: "control",
      payload: setCommand({ pitch_shift: Number(el.pitch.value) }),
    } satisfies ToWorker);
  });

  // Ретривал тоже меняется на лету, тем же control-сообщением.
  el.indexRatio.addEventListener("input", () => {
    renderIndexRatio(true);
    session?.worker.postMessage({
      type: "control",
      payload: setCommand({ index_ratio: Number(el.indexRatio.value) }),
    } satisfies ToWorker);
  });

  // Смена голоса — это переподключение, но ползунок индекса надо обновить
  // сразу: у разных голосов индекс есть не всегда.
  el.voice.addEventListener("change", syncIndexControl);

  // Подписи, подсказки и сохранение — на все ручки разом.
  settings.restore();
  settings.renderAll();
  installHelpDismiss();
  for (const [id, knob] of Object.entries(settings.knobs)) {
    attachHelp(id, knob.input);
    knob.input.addEventListener("input", () => {
      settings.renderKnob(knob);
      settings.save();

      // Защита согласных — единственная из этой группы, что применяется на
      // лету: сервер меняет её атомиком, как тон и индекс.
      if (id === "protect") {
        session?.worker.postMessage({
          type: "control",
          payload: setCommand({ protect: Number((knob.input as HTMLInputElement).value) }),
        } satisfies ToWorker);
      }
      if (id === "vad") {
        settings.syncVad();
      }
      // Шумодав решает не только за себя: он требует полной частоты и потому
      // запрещает экономию канала.
      if (id === "rnnoise" || id === "thrifty") {
        settings.syncThrifty();
      }
      // Последним: `syncVad` мог сбросить порог на умолчание нового режима.
      syncDerived();
    });
  }
  syncDerived();

  // Сохранение обоих списков делает общий цикл выше: устройства теперь такие
  // же ручки, как остальные.
  el.outputDevice.addEventListener("change", () => {
    // Выход переключается на лету — граф пересобирать не надо.
    session?.playback.setSink(el.outputDevice.value).catch(() => {
      showReason("Выбранный выход недоступен, играю в системный.");
    });
  });

  // Список устройств живой: наушники втыкают и вынимают, и увидеть это надо
  // без перезагрузки страницы. Пересобирать граф на каждое событие не будем —
  // обновляем только списки, и только перечислением, без запроса разрешения.
  //
  // Задержка не для красоты: открытие потока само порождает `devicechange`, и
  // без неё каждый запуск давал бы пачку лишних перечислений.
  let devicesTimer: ReturnType<typeof setTimeout> | undefined;
  navigator.mediaDevices.addEventListener("devicechange", () => {
    if (defaults === null) {
      return;
    }
    clearTimeout(devicesTimer);
    devicesTimer = setTimeout(() => void refreshDevices(), 500);
  });

  // Уход со страницы обязан гасить звук: иначе микрофон останется занят.
  window.addEventListener("pagehide", () => void stopSession());

  void restoreSession();
}

/**
 * Узнать у сервера, вошёл ли человек, и показать это.
 *
 * Автоматического ухода на Keycloak здесь нет намеренно: он уносил бы человека
 * со страницы, едва тот её открыл, а при живой сессии Keycloak нажатие «Войти»
 * и так проходит без формы.
 */
async function restoreSession(): Promise<void> {
  // Неудачный вход сервер возвращает сюда с причиной в адресе. Причину держим
  // для экрана входа и сразу вычищаем адрес: перезагрузка не должна повторять
  // старую ошибку.
  const failure = new URLSearchParams(location.search).get("auth_error");
  if (failure !== null) {
    history.replaceState(null, "", location.pathname);
  }

  let info;
  try {
    info = await fetchSession();
  } catch (error) {
    showGate(`Сервер не отвечает: ${String(error)}`);
    return;
  }

  // Сервер поднят с --insecure-no-auth: входить некуда и незачем.
  if (!info.enabled) {
    showPage();
    el.who.textContent = "вход не требуется";
    await loadCatalog();
    return;
  }

  if (!info.signed_in) {
    showGate(failure === null ? null : `Вход не удался: ${failure}`);
    return;
  }

  if (info.access === "forbidden") {
    showNoAccess();
    return;
  }

  showPage();
  el.who.textContent = info.username ?? "вход выполнен";
  el.logout.classList.remove("hidden");
  await loadCatalog();
}

/**
 * Выйти: сессию закрывает сервер, он же уводит на выход в Keycloak.
 *
 * POST, а не переход по ссылке: выход по GET можно подложить картинкой на
 * любую страницу. Форма, а не fetch: ответ — переход на выход в Keycloak, и
 * следовать ему должен сам браузер.
 */
function signOut(): void {
  // Микрофон гасим до ухода: страница сейчас сменится на Keycloak, и ждать
  // закрытия контекстов некогда — `close()` умеет не отвечать вовсе.
  void stopSession();

  const form = document.createElement("form");
  form.method = "post";
  form.action = "/auth/logout";
  document.body.append(form);
  form.submit();
}

/**
 * Экран входа: одна кнопка «Войти».
 *
 * Строка под ней — только когда что-то пошло не так. Без неё человек жал бы
 * кнопку, возвращался на ту же кнопку и не понимал бы почему.
 */
function showGate(reason: string | null): void {
  document.body.dataset.state = "signed-out";
  el.gateReason.textContent = reason ?? "";
  el.gateReason.classList.toggle("hidden", reason === null);
  el.login.classList.remove("hidden");
  el.login.disabled = false;
  el.gateLogout.classList.add("hidden");
}

/**
 * Вошёл, но доступа нет.
 *
 * Ни имени группы, ни советов: добавить себя в группу человек всё равно не
 * может, а состав групп чужого реалма — не его дело. Остаётся «Выйти», чтобы
 * войти под другой учёткой.
 */
function showNoAccess(): void {
  document.body.dataset.state = "no-access";
  el.gateReason.textContent = "Нет доступа";
  el.gateReason.classList.remove("hidden");
  el.login.classList.add("hidden");
  el.gateLogout.classList.remove("hidden");
}

function showPage(): void {
  document.body.dataset.state = "in";
}

main();

console.info(`rustvoice web, сборка ${__BUILD__}`);
